import React, { useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import DataGrid from 'components/common/data-grid/data-grid-component';
import { DataGridDataApi } from 'models/utils/common/data-grid/data-grid-data-model';
import Modal from 'components/common/modal-component';
import { ModalButton } from 'models/utils/common/modal-button-model';
import { batchUpdateElementRequest } from 'api/element-api';
import { updateElementCacheByCalloutAction } from 'store/actions/element-cache-actions';
import { fetchWorkpaperListForRevisionWithoutLoading } from 'store/actions/workpaper-toolkit-list-actions';
import { LEFT_PANELS } from 'constants/feature/panel-constants';
import BatchElementUpdateForm from 'models/form/batch-element-update-form-model';
import { attachWorkpaperListColumns } from 'constants/feature/statement-content-constants';
import { AUTO_TIEOUT_WORKPAPER_TYPE, AUTO_TIEOUT_TB_WORKPAPER_TYPE } from 'constants/feature/statement-creation-constants';

export const ATTACH_EXISTING_WORKPAPER_BLOCK = 'attach-existing-workpaper';

const AttachExistingWorkpaperModal = ({
  onClose,
  listData,
  elementDetails,
  revisionId,
}) => {
  const [selectedRows, setSelectedRows] = useState({});
  const dispatch = useDispatch();
  const leftPanel = useSelector((state) => state.ui.statementPage.panels.left);
  const socketHasBeenDisconnected = useSelector(
    (state) => state.sockets.statementSocket.socketHasBeenDisconnected,
  );
  const isLeftWorkpaperpanelActive = leftPanel === LEFT_PANELS.WORKPAPER;

  const onRowCheckboxSelect = (selectedRows) => {
    setSelectedRows(selectedRows);
  };

  const onSubmit = async () => {
    try {
      onClose();
      
      const selectedWorkpaperIds = Object.keys(selectedRows).filter(
        key => selectedRows[key]
      );

      const selectedWorkpapers = selectedWorkpaperIds.map(workpaperRefId => {
        return listData.find(wp => wp.workpaperRefId === parseInt(workpaperRefId));
      }).filter(Boolean);

      if (selectedWorkpapers.length > 0) {
        const batchUpdatesModel = new BatchElementUpdateForm({ isLoaded: true })
          .setWorkpaperList(selectedWorkpapers);

        await batchUpdateElementRequest({
          revisionId,
          updatesModel: batchUpdatesModel,
          elementIds: [elementDetails.id],
        });

        // Update element cache
        dispatch(
          updateElementCacheByCalloutAction({
            elementIds: [elementDetails.id],
            showCallout: true,
          }),
        );

        // Refresh workpaper list if needed
        if (isLeftWorkpaperpanelActive && socketHasBeenDisconnected) {
          dispatch(
            fetchWorkpaperListForRevisionWithoutLoading({
              revisionId,
            }),
          );
        }
      }
    } catch (error) {
      console.error('Error attaching workpapers:', error);
    }
  };

  const isAnyRowSelected = Object.values(selectedRows).some(Boolean);

  return (
    <Modal
      id={`${ATTACH_EXISTING_WORKPAPER_BLOCK}`}
      title={'quick-actions.attach-existing-workpaper-modal.title'}
      primaryModalButton={
        new ModalButton({
          text: 'common.save',
          onClick: onSubmit,
          disabled: !isAnyRowSelected,
        })
      }
      secondaryModalButton={
        new ModalButton({
          text: 'common.cancel',
          onClick: onClose,
        })
      }
      onClose={onClose}
    >
      <div>
          <DataGrid
            className={classnames(`${ATTACH_EXISTING_WORKPAPER_BLOCK}__list`)}
            columns={attachWorkpaperListColumns}
            tableId={`${ATTACH_EXISTING_WORKPAPER_BLOCK}-list`}
            scrollable={true}
            numberOfVisibleRows={2.5}
            dataModel={
              new DataGridDataApi({
                apiModel: { isLoaded: true },
                rowItems: listData
                  .filter(
                    (wp) =>
                      wp.workpaperType !== AUTO_TIEOUT_WORKPAPER_TYPE &&
                      wp.workpaperType !== AUTO_TIEOUT_TB_WORKPAPER_TYPE
                  )
                  .map((wp) => ({ ...wp, id: wp.workpaperRefId })),
              })
            }
            onRowCheckboxSelect={onRowCheckboxSelect}
            selectedRows={selectedRows}
            canSelectMoreThenOneRow={true}
          />
      </div>
    </Modal>
  );
};

export default AttachExistingWorkpaperModal;

AttachExistingWorkpaperModal.propTypes = {
  /** function fired to close the modal */
  onClose: PropTypes.func.isRequired,
  /** list of workpapers to display */
  listData: PropTypes.array.isRequired,
  /** element details for attaching workpapers */
  elementDetails: PropTypes.object.isRequired,
  /** revision id */
  revisionId: PropTypes.number.isRequired,
  /** array of already added workpaper IDs (deprecated - workpapers are now filtered out) */
  alreadyAddedWorkpaperIds: PropTypes.array,
};